import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';
import { Loader2, Lock, AlertTriangle, Eye, EyeOff, Sun, Moon } from 'lucide-react';

const LOGO_URL = "/logo-alphabit.png";

export default function ChangePasswordPage() {
  const [searchParams] = useSearchParams();
  const isFirst = searchParams.get('first') === 'true';
  const { api, user, logout } = useAuth();
  const { t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordsNotMatch'));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success(t('auth.passwordChanged'));
      
      // Redirect based on role
      if (user?.role === 'super_admin') {
        window.location.href = '/admin';
      } else if (user?.role === 'agent') {
        window.location.href = '/agent';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.changePasswordError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 sm:p-8 ${
      isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-br from-slate-50 to-slate-100'
    }`}>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AlphaBit" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl" />
            <span className={`font-heading font-bold text-xl sm:text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}>Bit</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleTheme}
              className={`w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                isDark 
                  ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSelector />
          </div>
        </div>

        <Card className={`shadow-xl ${
          isDark 
            ? 'bg-[#0F0F2F] border-purple-500/30' 
            : 'bg-white border-slate-200'
        }`}>
          <CardContent className="p-6 sm:p-8">
            {isFirst && (
              <div className={`mb-6 p-4 rounded-lg border ${
                isDark 
                  ? 'bg-amber-500/10 border-amber-500/30' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    isDark ? 'text-amber-400' : 'text-amber-600'
                  }`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>{t('auth.mandatoryChange')}</p>
                    <p className={`text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                      {t('auth.mandatoryChangeMessage')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className={`w-12 h-12 sm:w-14 sm:h-14 mb-4 sm:mb-6 rounded-xl flex items-center justify-center ${
              isDark ? 'bg-purple-500/20' : 'bg-primary/10'
            }`}>
              <Lock className={`w-6 h-6 sm:w-7 sm:h-7 ${isDark ? 'text-[#9D4DFF]' : 'text-primary'}`} />
            </div>
            
            <h1 className={`font-heading text-xl sm:text-2xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>
              {t('auth.changePasswordTitle')}
            </h1>
            <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              {t('auth.changePasswordSubtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className={isDark ? 'text-gray-300' : ''}>{t('auth.currentPassword')}</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrent ? "text" : "password"}
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password"
                    className={`pr-10 h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword" className={isDark ? 'text-gray-300' : ''}>{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNew ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="new-password"
                    className={`pr-10 h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{t('auth.passwordMinLength')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className={isDark ? 'text-gray-300' : ''}>{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password"
                    className={`pr-10 h-11 rounded-xl ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className={`w-full h-11 rounded-xl ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF]' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                disabled={loading}
                data-testid="change-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('auth.changing')}
                  </>
                ) : (
                  t('auth.changeButton')
                )}
              </Button>
            </form>

            {!isFirst && (
              <Button 
                variant="ghost" 
                className={`w-full mt-4 ${isDark ? 'text-gray-400 hover:text-white hover:bg-purple-500/10' : ''}`}
                onClick={() => navigate(-1)}
              >
                {t('common.cancel')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
